import {Component, OnInit} from "@angular/core";
import {IUserActionDialogData, UserActionDialogComponent,} from "./user-action/user-action-dialog.component";
import {MatDialog} from "@angular/material/dialog";
import {NotificationService} from "../../../core/services/notification.service";
import {
  ConfirmationDialogComponent,
  IConfirmationDialogData,
} from "../../../shared/components/confirmation-dialog/confirmation-dialog.component";
import {UsersControllerService} from "../../../core/api/controllers/users-controller.service";
import {LocationsControllerService} from "../../../core/api/controllers/locations-controller.service";
import {IUser, IUserLocation} from "../../../core/models/subscription.model";
import {ILocation} from "../../../shared/interfaces/ILocation";
import {EAction} from "../../../shared/enums/EAction";
import {LocationBadgeComponent} from "./location-badge/location-badge.component";
import {MatMenuModule} from "@angular/material/menu";
import {MatButtonModule} from "@angular/material/button";
import {NgClass, NgForOf, NgIf} from "@angular/common";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {FormsModule} from "@angular/forms";
import {SharedModule} from "../../../shared/shared.module";

@Component({
  selector: "app-team",
  templateUrl: "./team.component.html",
  styleUrls: ["./team.component.scss"],
  standalone: true,
  imports: [LocationBadgeComponent, MatMenuModule, MatButtonModule, NgClass, NgForOf, NgIf, MatProgressSpinnerModule, SharedModule, FormsModule]
})
export class TeamComponent implements OnInit {
  private static readonly VISIBLE_LOCATIONS = 3;

  users: IUser[] = [];
  locations: ILocation[] = [];
  currentUser: IUser;
  isLoading = false;
  searchTerm = "";
  readonly skeletonRows = [0, 1, 2, 3];

  private expandedLocations = new Set<string>();

  constructor(
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private usersController: UsersControllerService,
    private locationsController: LocationsControllerService,
  ) {}

  ngOnInit(): void {
    this.getLocations();
  }

  get filteredUsers(): IUser[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.users;
    }
    return this.users.filter((user) =>
      [
        `${user.firstName} ${user.lastName}`,
        user.username,
        user.email,
        ...(user.userLocations ?? []).map((location) => location.name),
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term)),
    );
  }

  get activeUsersCount(): number {
    return this.users.filter((user) => user.isActive).length;
  }

  get disabledUsersCount(): number {
    return this.users.length - this.activeUsersCount;
  }

  clearSearch(): void {
    this.searchTerm = "";
  }

  trackByUserId(index: number, user: IUser): string {
    return user.id;
  }

  initials(user: IUser): string {
    const letters = `${user.firstName?.charAt(0) ?? ""}${user.lastName?.charAt(0) ?? ""}`;
    return letters.trim() || user.username?.charAt(0) || "?";
  }

  /** Stable per-user colour so avatars stay recognisable between refreshes. */
  avatarTone(user: IUser): number {
    const source = user.id || user.username || "";
    let hash = 0;
    for (let i = 0; i < source.length; i++) {
      hash = (hash + source.charCodeAt(i)) % 6;
    }
    return hash;
  }

  visibleLocations(user: IUser): IUserLocation[] {
    const userLocations = user.userLocations ?? [];
    return this.isLocationsExpanded(user)
      ? userLocations
      : userLocations.slice(0, TeamComponent.VISIBLE_LOCATIONS);
  }

  hiddenLocationsCount(user: IUser): number {
    if (this.isLocationsExpanded(user)) {
      return 0;
    }
    return Math.max(
      (user.userLocations?.length ?? 0) - TeamComponent.VISIBLE_LOCATIONS,
      0,
    );
  }

  isLocationsExpanded(user: IUser): boolean {
    return this.expandedLocations.has(user.id);
  }

  toggleLocations(user: IUser): void {
    if (this.expandedLocations.has(user.id)) {
      this.expandedLocations.delete(user.id);
    } else {
      this.expandedLocations.add(user.id);
    }
  }

  private userDialogConfig(data: IUserActionDialogData) {
    return {
      data,
      width: "620px",
      maxWidth: "94vw",
      autoFocus: false,
      panelClass: "user-action-dialog-panel",
    };
  }

  addUser(): void {
    const data: IUserActionDialogData = {
      action: EAction.Add,
    };
    this.dialog
      .open(UserActionDialogComponent, this.userDialogConfig(data))
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.getUsers();
        }
      });
  }

  toggleUserStatus(user: IUser): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: `${user.isActive ? "Disable" : "Enable"} ${user.username}`,
          message: `Are you sure you want to ${user.isActive ? "disable" : "enable"} user: ${user.username}?`,
        } as IConfirmationDialogData,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.confirmChangeUserStatus(user);
        }
      });
  }

  confirmChangeUserStatus(user: IUser): void {
    this.isLoading = true;
    this.usersController.changeUserStatus(user.id, !user.isActive).subscribe({
      next: () => {
        this.notificationService.success(`User ${user.isActive ? 'disabled' : 'enabled'} successfully.`);
        this.getUsers();
      },
      error: (error) => {
        this.notificationService.error(error.error);
        this.isLoading = false;
      },
    });
  }

  getLocations(): void {
    this.isLoading = true;
    this.locationsController.getAllLocations().subscribe(
      (response) => {
        this.locations = response.locations;
        this.locations = this.locations.map((location) => {
          return {
            ...location,
            ips: ["192.168.1.1", "172.192.15.13", "152.145.161.2"],
            collapsed: false,
          };
        });
        this.getUsers();
      },
      (error) => {
        this.notificationService.error(error);
        this.isLoading = false;
      },
    );
  }

  getUsers(): void {
    this.isLoading = true;
    this.usersController.getUsers().subscribe(
      (response) => {
        this.users = response;
        this.isLoading = false;
      },
      (error) => {
        this.notificationService.error(error);
        this.isLoading = false;
      },
    );
  }

  deleteUser(currentUser: any): void {
    this.currentUser = currentUser;
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: "Delete " + currentUser.username,
          message: `Are you sure you want to delete user: ${currentUser.username}?`,
        } as IConfirmationDialogData,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.confirmDeleteUser();
          this.getUsers();
        }
      });
  }

  confirmDeleteUser() {
    this.usersController.deleteUser(this.currentUser.id).subscribe(
      (response) => {
        if (response.id) {
          this.notificationService.success(
            `User deleted: ${this.currentUser.username}`,
          );
        } else {
          this.notificationService.error("We could not delete this user");
        }
        this.currentUser = null;
      },
      (error) => {
        this.notificationService.error(error);
      },
    );
  }

  editUser(currentUser: any) {
    const data: IUserActionDialogData = {
      action: EAction.Edit,
      currentUser,
    };
    this.dialog
      .open(UserActionDialogComponent, this.userDialogConfig(data))
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.getUsers();
        }
      });
  }
}
